import { useCallback, useEffect, useRef, useState } from 'react';
import AxiosFactory from '../../services/http.service';
import ApiRoutes from '../../config/api.routes';
import { IpfsDocument } from './ipfsDocumentTypes';

interface UseWetInkSignerOptions {
  documentId: number;
}

/**
 * Outcome of a scan upload. The two 400 failure modes are structurally different and call for
 * different user actions (wet-ink guide §5): a `tampered` verdict means re-download + re-sign, a
 * `transient` service outage means retry the same upload shortly.
 */
export type WetInkUploadOutcome =
  | { kind: 'success'; document: IpfsDocument }
  | { kind: 'tampered'; message: string; summary?: string; discrepancies?: string[] }
  | { kind: 'transient'; message: string }
  | { kind: 'error'; message: string };

/**
 * Classify a failed scan upload into the right outcome (guide §5). A tampered verdict (has
 * `summary`/`discrepancies`) means re-download + re-sign; a "temporarily unavailable" message is a
 * transient service outage to retry as-is; anything else is a guard failure to surface directly.
 */
export function parseWetInkUploadError(e: any): WetInkUploadOutcome {
  const data = e?.response?.data ?? {};
  const message: string = data.message || e?.message || 'Failed to upload the signed scan.';

  if (Array.isArray(data.discrepancies) || data.summary) {
    return {
      kind: 'tampered',
      message,
      summary: data.summary,
      discrepancies: Array.isArray(data.discrepancies) ? data.discrepancies : undefined,
    };
  }
  if (/temporarily unavailable/i.test(message)) {
    return { kind: 'transient', message };
  }
  return { kind: 'error', message };
}

/**
 * Drives the wet-ink signing flow for one required signer on a document (wet-ink guide §3):
 * download the exact PDF to print, then upload the scanned, hand-signed file for automated
 * integrity verification. Distinguishes the tampered vs. temporarily-unavailable failures so the
 * UI can react appropriately.
 */
export function useWetInkSigner({ documentId }: UseWetInkSignerOptions) {
  const [document, setDocument] = useState<IpfsDocument | null>(null);
  const [loadingDoc, setLoadingDoc] = useState(true);
  const [docError, setDocError] = useState<string | null>(null);

  // The exact reference PDF fetched from download-for-signing, exposed as a local blob URL.
  const [referenceUrl, setReferenceUrl] = useState<string>('');
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const blobUrlRef = useRef<string>('');

  const [uploadLoading, setUploadLoading] = useState(false);
  const [outcome, setOutcome] = useState<WetInkUploadOutcome | null>(null);

  const refreshDocument = useCallback(async () => {
    try {
      const { axios } = new AxiosFactory();
      const res = await axios.get<IpfsDocument>(ApiRoutes.ipfsDocument.get(documentId));
      setDocument(res.data);
      return res.data;
    } catch (err: any) {
      setDocError(err?.response?.data?.message || err?.message || 'Failed to load document.');
      return null;
    }
  }, [documentId]);

  useEffect(() => {
    setLoadingDoc(true);
    refreshDocument().finally(() => setLoadingDoc(false));
  }, [refreshDocument]);

  useEffect(
    () => () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = '';
      }
    },
    []
  );

  /**
   * Fetch the exact document to print. Always re-download before (re-)signing — the reference can
   * change if another party fills a field in the meantime (wet-ink guide §5). Exposes a blob URL
   * for preview and also triggers a browser download so the signer can print it.
   */
  const handleDownload = useCallback(async () => {
    setDownloadLoading(true);
    setDownloadError(null);
    try {
      const { axios } = new AxiosFactory();
      const res = await axios.get<ArrayBuffer>(
        ApiRoutes.ipfsDocument.downloadForSigning(documentId),
        { responseType: 'arraybuffer' }
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      if (blobUrlRef.current) {URL.revokeObjectURL(blobUrlRef.current);}
      blobUrlRef.current = url;
      setReferenceUrl(url);

      // Trigger a browser download so the signer has a copy to print and sign.
      const a = window.document.createElement('a');
      a.href = url;
      a.download = `document-${documentId}-to-sign.pdf`;
      a.click();
    } catch (e: any) {
      setDownloadError(
        e?.response?.data?.message || e?.message || 'Failed to download the document for signing.'
      );
    } finally {
      setDownloadLoading(false);
    }
  }, [documentId]);

  /** Upload a scanned, hand-signed file (multipart field `scan`) for automated verification. */
  const handleUpload = useCallback(
    async (scan: File) => {
      setUploadLoading(true);
      setOutcome(null);
      try {
        const form = new FormData();
        form.append('scan', scan);

        const { axios } = new AxiosFactory();
        const res = await axios.post<IpfsDocument>(
          ApiRoutes.ipfsDocument.wetInkSignature(documentId),
          form,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        setDocument(res.data);
        const result: WetInkUploadOutcome = { kind: 'success', document: res.data };
        setOutcome(result);
        return result;
      } catch (e: any) {
        const result = parseWetInkUploadError(e);
        setOutcome(result);
        return result;
      } finally {
        setUploadLoading(false);
      }
    },
    [documentId]
  );

  const clearOutcome = useCallback(() => setOutcome(null), []);

  return {
    document,
    loadingDoc,
    docError,
    referenceUrl,
    downloadLoading,
    downloadError,
    handleDownload,
    uploadLoading,
    outcome,
    handleUpload,
    clearOutcome,
    refreshDocument,
  };
}
